// New-lead → "Create CRM record?" prompt.
//
// When a new lead surfaces (a Kit "New lead" alert, or a new-submission
// notification) and NO CRM contact record exists for it yet, we offer to create
// one in a single tap. The confirm prompt shows the lead's name + email; on
// "Create record" we POST the lead's details to /api/crm/contacts (stage =
// lead), then open the freshly-created record so the retailer can pick up the
// conversation. "Maybe later" simply dismisses — it should feel like the natural
// next step, not an interruption.
//
// Shared by shell/kit-alerts.ts (alert action) and shell/shell.ts (notification
// click). Uses the global toast + confirmDialog so callers don't have to thread
// UI plumbing through their signatures.

import { api, ApiError } from "@tkos/api-client";
import { confirmDialog, toast } from "@tkos/shared-ui";
import { navigate } from "./router";

export interface LeadSeed {
  /** If the lead already has a CRM record, callers should NOT prompt. */
  customerId?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  budget?: string | null;
  notes?: string | null;
}

interface PostResponse {
  ok?: boolean;
  contacts?: Array<{ id?: string; name?: string }>;
  error?: string;
}

/**
 * Prompt to create a CRM record for a lead that has none, then create + open it.
 * No-op (resolves false) if the user picks "Maybe later" or creation fails.
 */
export async function promptCreateCrmRecord(lead: LeadSeed): Promise<boolean> {
  const display =
    (lead.name || "").trim() || (lead.email || "").trim() || (lead.phone || "").trim() || "this lead";
  const emailLine = lead.email ? ` (${lead.email.trim()})` : "";

  const ok = await confirmDialog(
    `Create a CRM record for ${display}${emailLine}? We'll pre-fill it with the lead's details so you can pick up the conversation.`,
    {
      title: "New lead",
      confirmLabel: "Create record",
      cancelLabel: "Maybe later",
    },
  );
  if (!ok) return false;

  try {
    const contact: Record<string, unknown> = {
      name: (lead.name || "").trim() || display,
      stage: "lead",
    };
    if (lead.email) contact["email"] = lead.email.trim();
    if (lead.phone) contact["phone"] = lead.phone.trim();
    if (lead.budget) contact["budget"] = String(lead.budget).trim();
    if (lead.notes) contact["notes"] = String(lead.notes).trim();

    const resp = await api<PostResponse>("/crm/contacts", {
      method: "POST",
      body: JSON.stringify({ contact }),
    });
    if (!resp || resp.ok === false) {
      throw new Error(resp?.error || "Could not create the record.");
    }

    const created = resp.contacts?.[0];
    toast.success("CRM record created.");
    // Let any open CRM page refresh, mirroring the new-customer modal.
    window.dispatchEvent(new CustomEvent("tkos:customer-created", { detail: { contact: created } }));

    if (created?.id) navigate("customer", { id: created.id });
    else navigate("crm");
    return true;
  } catch (err) {
    const msg =
      err instanceof ApiError
        ? `Server error (HTTP ${err.status})`
        : err instanceof Error
          ? err.message
          : "Could not create the record.";
    toast.error(msg);
    return false;
  }
}
